import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useReducedMotion as fmUseReducedMotion } from "framer-motion";

/**
 * CareLogix™ ORACLE — Animation Settings (Phase 30B).
 *
 * Centralised motion preferences for the Animated Market Intelligence Experience
 * Pack. Reads OS-level `prefers-reduced-motion`, layers a per-user override
 * persisted to localStorage, and exposes a clean React context.
 *
 * Defaults are deliberately conservative on mobile to avoid jank.
 * All gating logic asks `enabled` — if it is false, all visual modules either
 * render their static fallback or hide their motion entirely.
 *
 * Strictly additive — no existing styling is touched.
 */

const LS_KEY = "carelogix.animation.prefs";
const BC_NAME = "carelogix:animation-prefs";

export const ANIMATION_DEFAULTS = Object.freeze({
  enabled:           true,
  reduceMotion:      false,
  chyronSpeed:       1,        // 0.5 = slow, 1 = default, 2 = fast
  voiceEnabled:      true,
  dockEnabled:       true,
  marketReplay:      true,
  globalMap:         true,
  floatingCandles:   true,
  marketPulseHero:   true,
  dailyInsightCards: true,
  sectorHeatmap:     true,
  signalFlow:        true,
});

const Ctx = React.createContext({
  prefs:          ANIMATION_DEFAULTS,
  enabled:        true,
  setPref:        () => {},
  setPrefs:       () => {},
  pause:          () => {},
  resume:         () => {},
  isPaused:       false,
  toggleSetting:  () => {},
  reduceMotion:   false,
});

function readPrefs() {
  if (typeof window === "undefined") return ANIMATION_DEFAULTS;
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return ANIMATION_DEFAULTS;
    const obj = JSON.parse(raw);
    return { ...ANIMATION_DEFAULTS, ...(obj || {}) };
  } catch (_) { return ANIMATION_DEFAULTS; }
}

export function AnimationSettingsProvider({ children }) {
  const [prefs, setPrefsState] = useState(readPrefs);
  const fmReduce = fmUseReducedMotion();
  const reduceMotion = Boolean(prefs.reduceMotion) || Boolean(fmReduce);
  const enabled = Boolean(prefs.enabled) && !reduceMotion;

  // Persist + broadcast on every change.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try { window.localStorage.setItem(LS_KEY, JSON.stringify(prefs)); } catch (_) { /* ignore */ }
    try {
      const ev = new CustomEvent(BC_NAME, { detail: prefs });
      window.dispatchEvent(ev);
    } catch (_) { /* ignore */ }
  }, [prefs]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onCustom = (e) => {
      if (e && e.detail) setPrefsState((p) => ({ ...p, ...e.detail }));
    };
    const onStorage = (e) => {
      if (!e || e.key !== LS_KEY || !e.newValue) return;
      try { setPrefsState(JSON.parse(e.newValue)); } catch (_) { /* ignore */ }
    };
    window.addEventListener(BC_NAME, onCustom);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(BC_NAME, onCustom);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const setPref = useCallback((key, value) => {
    setPrefsState((p) => ({ ...p, [key]: value }));
  }, []);
  const setPrefs = useCallback((next) => {
    setPrefsState((p) => ({ ...p, ...(next || {}) }));
  }, []);
  const pause   = useCallback(() => setPrefsState((p) => ({ ...p, enabled: false })), []);
  const resume  = useCallback(() => setPrefsState((p) => ({ ...p, enabled: true })), []);
  const toggleSetting = useCallback((key) => setPrefsState((p) => ({ ...p, [key]: !p[key] })), []);

  const value = useMemo(() => ({
    prefs,
    enabled,
    reduceMotion,
    isPaused: !prefs.enabled,
    setPref,
    setPrefs,
    pause,
    resume,
    toggleSetting,
  }), [prefs, enabled, reduceMotion, setPref, setPrefs, pause, resume, toggleSetting]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAnimationSettings() { return React.useContext(Ctx); }

/**
 * Convenience wrapper: returns true when *both* the OS prefers reduced motion
 * AND the user hasn't explicitly opted back into full motion via AdminOne.
 */
export function useReducedMotionSafe() {
  const { reduceMotion } = useAnimationSettings();
  return reduceMotion;
}
